import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, resolveApiAssetUrl } from '../lib/api'
import {
  filterMonthlyBriefImagesByKind,
  sortMonthlyBriefImagesBySlideOrder,
  findSlideMetaForMonthlyBriefImage,
  parseStorageSlideIdFromMonthlyBriefImageName,
} from '../lib/monthlyBriefCardNews'
import LeadPrivacyConsent from './categoryLanding/LeadPrivacyConsent'
import { splitTags } from '../features/publicPage/registry'
import { normalizePersonalLandingState } from '../features/personalLanding/normalize'
import {
  PersonalLandingOrgHero,
  renderPersonalLandingBodySection,
} from '../features/personalLanding/PublicLandingRender'
import {
  buildPersonalLandingNavItems,
  PersonalLandingOrgFooter,
  PersonalLandingOrgNav,
  PersonalLandingOrgStyles,
  usePersonalLandingScrollNav,
} from '../features/personalLanding/personalLandingOrgChrome'
import { usePersonalLandingMonthlyIssue } from '../features/personalLanding/usePersonalLandingMonthlyIssue'

function dedupeOrgParts(parts) {
  const out = []
  const seen = new Set()
  for (const p of parts || []) {
    const v = p == null ? '' : String(p).trim()
    if (!v) continue
    if (seen.has(v)) continue
    seen.add(v)
    out.push(v)
  }
  return out
}

/**
 * 로컬 초안 데이터로 공개 소개페이지와 동일한 구조를 미리보기(항상 preview 모드).
 */
export default function PersonalLandingDraftPreview({ data }) {
  const code = data?.code ? String(data.code) : ''
  const monthlyIssue = usePersonalLandingMonthlyIssue(data)

  const consentQuery = useQuery({
    queryKey: ['public-leads-consents', code],
    enabled: Boolean(code),
    retry: false,
    queryFn: async () => {
      const cRes = await api.get(`/public/leads/${encodeURIComponent(code)}/consents`, { __silent: true })
      return (cRes?.data && cRes.data.data !== undefined) ? cRes.data.data : cRes.data
    },
  })

  const [issueModal, setIssueModal] = React.useState({ open: false, kind: 'life', idx: 0, list: [] })

  const openPublicIssueModal = React.useCallback((kind, startIdx = 0) => {
    const filtered = sortMonthlyBriefImagesBySlideOrder(
      filterMonthlyBriefImagesByKind(monthlyIssue.images, kind),
      monthlyIssue.brief?.payload,
      kind
    )
    if (!filtered.length) return
    const i = Math.max(0, Math.min(Number(startIdx) || 0, filtered.length - 1))
    setIssueModal({ open: true, kind, idx: i, list: filtered })
  }, [monthlyIssue.images, monthlyIssue.brief?.payload])

  const closePublicIssueModal = React.useCallback(() => {
    setIssueModal({ open: false, kind: 'life', idx: 0, list: [] })
  }, [])

  const { isScrolled, mobileMenuOpen, setMobileMenuOpen } = usePersonalLandingScrollNav()

  const navItems = React.useMemo(() => {
    if (!data) return []
    try {
      const { sections } = normalizePersonalLandingState(data?.landingSections, data, data?.landingPageStyle)
      return buildPersonalLandingNavItems(sections)
    } catch {
      return []
    }
  }, [data])

  const scrollToTop = React.useCallback(() => {
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch {
      window.scrollTo(0, 0)
    }
  }, [])

  const scrollToAnchor = React.useCallback((anchorId) => {
    if (!anchorId) return
    const el = document.getElementById(anchorId)
    if (el?.scrollIntoView) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
    setMobileMenuOpen(false)
  }, [setMobileMenuOpen])

  const [leadOpen, setLeadOpen] = React.useState(false)
  const [leadName, setLeadName] = React.useState('')
  const [leadPhone, setLeadPhone] = React.useState('')
  const [leadTime, setLeadTime] = React.useState('')
  const [leadMsg, setLeadMsg] = React.useState('')
  const [leadPrivacyAccepted, setLeadPrivacyAccepted] = React.useState(false)
  const [submitting] = React.useState(false)
  const [submitOk] = React.useState(false)
  const [submitErr, setSubmitErr] = React.useState('')
  const [website, setWebsite] = React.useState('')

  const isPreview = true

  if (!data) {
    return (
      <div className="flex min-h-[240px] items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white text-sm text-slate-500">
        미리보기 데이터가 없습니다.
      </div>
    )
  }

  const name = data?.name || ''
  const org = dedupeOrgParts([data?.orgRootName, data?.orgParentName, data?.orgName]).join(' ')
  const phone = data?.phoneNumber || ''
  const email = data?.email || ''
  const mobileFaxNumber = data?.mobileFaxNumber || ''
  const kakaoChatUrl = data?.kakaoChatUrl || ''
  const activity = data?.activityAreaText || ''
  const career = data?.careerText || ''
  const freeText = data?.freeText || ''
  const profileImageUrl = data?.profileImageUrl || ''
  const landingGalleryEnabled = Boolean(data?.landingGalleryEnabled)
  const landingGalleryUrls = Array.isArray(data?.landingGalleryUrls) ? data.landingGalleryUrls.filter(Boolean) : []
  const showCareerCard = data?.showCareerCard !== false
  const showActivityAreaCard = data?.showActivityAreaCard !== false
  const showFreeTextCard = data?.showFreeTextCard !== false
  const imageCardEnabled = Boolean(data?.imageCardEnabled)
  const imageCardUrl = data?.imageCardUrl || ''
  const youtubeUrl = data?.youtubeUrl || ''
  const instagramUrl = data?.instagramUrl || ''
  const blogUrl = data?.blogUrl || ''
  const tags = splitTags(data?.serviceTags)
  const landingState = normalizePersonalLandingState(data?.landingSections, data, data?.landingPageStyle)
  const landingSections = landingState.sections
  const landingPageStyle = landingState.style || {}
  const heroSection = landingSections.find((s) => s.type === 'hero') || null
  const landingSkin = data?.landingTemplateId || 'LANDING_V1'
  const cardUrlPath = data?.code ? `/@${data.code}` : ''

  const consentInfo = consentQuery.data || null

  const fallbackConsentText = '개인정보 수집·이용에 동의합니다. (수집 항목: 이름, 연락처, (선택)희망연락시간, (선택)문의내용 / 이용 목적: 상담 신청 접수 및 상담 진행 / 보유·이용 기간: 접수일로부터 30일)'
  const receiver = [name, org].filter(Boolean).join(' / ')
  const fallbackThirdConsentText = `개인정보 제3자 제공에 동의합니다. (제공받는 자: ${receiver || '해당 설계사'} / 제공 항목: 이름, 연락처, (선택)희망연락시간, (선택)문의내용 / 제공 목적: 상담 진행 / 보유·이용 기간: 접수일로부터 30일)`

  const consentFormat = consentInfo?.collectUse?.format || 'TEXT'
  const consentText = consentInfo?.collectUse?.text || fallbackConsentText
  const thirdConsentFormat = consentInfo?.thirdParty?.format || 'TEXT'
  const thirdConsentText = consentInfo?.thirdParty?.text || fallbackThirdConsentText

  const contactHref = kakaoChatUrl || (phone ? `tel:${phone.replace(/[^0-9+]/g, '')}` : null)
  const phoneDigits = phone ? phone.replace(/[^0-9+]/g, '') : ''
  const smsHref = phoneDigits ? `sms:${phoneDigits}` : ''

  const landingAccentStyle = landingPageStyle?.primaryColor
    ? { '--landing-accent': String(landingPageStyle.primaryColor) }
    : undefined

  const primaryColor = landingPageStyle?.primaryColor || '#0f172a'

  const bodySectionCtx = {
    data,
    tags,
    monthlyIssue,
    openPublicIssueModal,
    landingGalleryEnabled,
    landingGalleryUrls,
    imageCardEnabled,
    imageCardUrl,
    showCareerCard,
    career,
    showActivityAreaCard,
    activity,
    showFreeTextCard,
    freeText,
    isPreview,
    setLeadOpen,
    contactHref,
    phone,
    email,
    mobileFaxNumber,
    kakaoChatUrl,
    cardUrlPath,
    layoutVariant: 'org',
    primaryColor,
  }

  const submitLead = (e) => {
    e.preventDefault()
    setSubmitErr('미리보기에서는 상담 신청 제출이 불가합니다.')
  }

  return (
    <div
      className={`min-h-screen text-slate-900 flex flex-col ${landingSkin === 'LANDING_V2' ? 'bg-slate-50' : 'bg-white'}`}
      data-landing-skin={landingSkin}
      style={landingAccentStyle}
    >
      <PersonalLandingOrgStyles />
      <PersonalLandingOrgNav
        displayName={name || '소개'}
        navItems={navItems}
        isScrolled={isScrolled}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        scrollToTop={scrollToTop}
        scrollToAnchor={scrollToAnchor}
      />
      <main className="flex-1">
        <PersonalLandingOrgHero
          heroSection={heroSection}
          data={data}
          profileImageUrl={profileImageUrl}
          landingGalleryEnabled={landingGalleryEnabled}
          landingGalleryUrls={landingGalleryUrls}
          youtubeUrl={youtubeUrl}
          instagramUrl={instagramUrl}
          blogUrl={blogUrl}
          phoneDigits={phoneDigits}
          smsHref={smsHref}
          primaryColor={primaryColor}
          isPreview={isPreview}
          onPrimaryCta={() => setLeadOpen(true)}
        />

        {issueModal.open && issueModal.list.length > 0 && (
          <div className="fixed inset-0 z-[65] flex items-center justify-center p-4">
            <button
              type="button"
              className="absolute inset-0 bg-black/60"
              onClick={closePublicIssueModal}
              aria-label="닫기"
            />
            <div className="relative flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
              <div className="flex items-center justify-between gap-2 border-b px-4 py-3">
                <div className="text-sm font-bold text-slate-900">
                  카드뉴스 ·{' '}
                  {monthlyIssue.periodLabel
                    || (monthlyIssue.brief?.year && monthlyIssue.brief?.month
                      ? `${monthlyIssue.brief.year}.${String(monthlyIssue.brief.month).padStart(2, '0')}`
                      : '—')}
                  {' · '}
                  {issueModal.kind === 'life' ? '생명보험' : '손해보험'}
                </div>
                <button
                  type="button"
                  className="text-slate-400 hover:text-slate-600 text-xl leading-none"
                  onClick={closePublicIssueModal}
                  aria-label="닫기"
                >
                  ✕
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50 p-4">
                {(() => {
                  const cur = issueModal.list[issueModal.idx]
                  const storageSid = parseStorageSlideIdFromMonthlyBriefImageName(cur?.imageName)
                  const slideMeta =
                    monthlyIssue.brief?.payload && storageSid
                      ? findSlideMetaForMonthlyBriefImage(monthlyIssue.brief.payload, issueModal.kind, storageSid)
                      : null
                  return (
                    <>
                      {cur?.fileId ? (
                        <img
                          src={resolveApiAssetUrl(cur.fileId)}
                          alt={slideMeta?.title || `카드뉴스 ${issueModal.idx + 1}`}
                          className="w-full max-h-[52vh] rounded-lg border border-slate-200 bg-white object-contain object-center shadow-sm"
                          loading="eager"
                          decoding="async"
                        />
                      ) : null}
                      {slideMeta && (slideMeta.title || slideMeta.body) ? (
                        <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4 text-left shadow-sm">
                          {slideMeta.title ? (
                            <div className="text-base font-bold text-slate-900 mb-2">{slideMeta.title}</div>
                          ) : null}
                          {slideMeta.body ? (
                            <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{slideMeta.body}</div>
                          ) : null}
                        </div>
                      ) : null}
                    </>
                  )
                })()}
              </div>
              <div className="flex items-center justify-between gap-2 border-t bg-white px-4 py-3">
                <button
                  type="button"
                  className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                  onClick={() => setIssueModal((m) => ({ ...m, idx: Math.max(0, m.idx - 1) }))}
                  disabled={issueModal.idx <= 0}
                >
                  이전
                </button>
                <span className="text-sm text-slate-600">
                  {issueModal.idx + 1} / {issueModal.list.length}
                </span>
                <button
                  type="button"
                  className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                  onClick={() =>
                    setIssueModal((m) => ({
                      ...m,
                      idx: Math.min(m.list.length - 1, m.idx + 1),
                    }))
                  }
                  disabled={issueModal.idx >= issueModal.list.length - 1}
                >
                  다음
                </button>
              </div>
            </div>
          </div>
        )}

        {leadOpen && (
          <div className="fixed inset-0 z-[60]">
            <button type="button" className="absolute inset-0 bg-black/40" onClick={() => setLeadOpen(false)} aria-label="닫기" />
            <div className="relative mx-auto max-w-xl px-4 py-10">
              <div className="rounded-2xl bg-white shadow-xl border overflow-hidden">
                <div className="flex items-center justify-between gap-2 px-5 py-4 border-b">
                  <div className="font-bold">상담 문의하기</div>
                  <button type="button" className="rounded border px-3 py-2 text-sm hover:bg-slate-50" onClick={() => setLeadOpen(false)}>닫기</button>
                </div>
                <div className="p-5">
                  {submitOk ? (
                    <div className="rounded-lg border bg-green-50 border-green-200 px-4 py-3 text-sm text-green-800">
                      접수되었습니다. 감사합니다.
                    </div>
                  ) : (
                    <form className="space-y-3" onSubmit={submitLead}>
                      <input
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        value={website}
                        onChange={(e) => setWebsite(e.target.value)}
                        className="hidden"
                        aria-hidden="true"
                      />

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-slate-600">이름 *</label>
                          <input className="mt-1 w-full rounded border px-3 py-2" value={leadName} onChange={(e) => setLeadName(e.target.value)} maxLength={80} />
                        </div>
                        <div>
                          <label className="block text-xs text-slate-600">연락처 *</label>
                          <input className="mt-1 w-full rounded border px-3 py-2" value={leadPhone} onChange={(e) => setLeadPhone(e.target.value)} maxLength={30} placeholder="01012345678" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-slate-600">희망 연락 시간(선택)</label>
                        <input className="mt-1 w-full rounded border px-3 py-2" value={leadTime} onChange={(e) => setLeadTime(e.target.value)} maxLength={80} placeholder="예) 평일 10~12시" />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-600">문의 내용(선택)</label>
                        <textarea className="mt-1 w-full rounded border px-3 py-2 min-h-[90px]" value={leadMsg} onChange={(e) => setLeadMsg(e.target.value)} maxLength={500} />
                      </div>
                      <LeadPrivacyConsent
                        collectUse={{ format: consentFormat, text: consentText }}
                        thirdParty={
                          consentInfo?.thirdParty?.text
                            ? consentInfo.thirdParty
                            : { format: thirdConsentFormat, text: thirdConsentText }
                        }
                        checked={leadPrivacyAccepted}
                        onChange={setLeadPrivacyAccepted}
                        accentColor={primaryColor}
                      />
                      {submitErr && <div className="text-xs text-red-600">{submitErr}</div>}
                      <button
                        type="submit"
                        className="w-full rounded-lg font-semibold py-3 bg-slate-100 text-slate-400"
                        disabled={submitting || isPreview}
                      >
                        미리보기 모드(제출 불가)
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {landingSections
          .filter((section) => section.type !== 'hero')
          .map((section) => renderPersonalLandingBodySection(section, bodySectionCtx))}
      </main>
      <PersonalLandingOrgFooter name={name} />
    </div>
  )
}
